import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "react-oidc-context";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Loader2, UsersRound, X } from "@fg/icons";
import { connectParityApi, normalizeUserId, type MeetingKind } from "@/api/connectApi";
import { useAccessToken } from "@/auth/useAccessToken";
import {
    BREAKOUT_SUBJECT_PREFIX, breakoutDescription, commandHtml, parseCommand,
    type BreakoutCommand, type BreakoutRoom
} from "@/lib/breakout";
import { cn } from "@/lib/utils";

export const OPEN_BREAKOUT_EVENT = "fg:breakout-open";

interface Person { userId: string; displayName: string }

/**
 * Breakout rooms inside a meeting room. The host splits the people in the room into rooms (automatically or by hand); each
 * room is its own meeting, so video and chat already work. People are invited with a prompt to join; "Return to main meeting"
 * brings them back, and "Close all rooms" calls everyone back after a short countdown.
 *
 *   in the main meeting: the host's dialog (open the Breakout rooms menu item) and the prompt for people who were assigned;
 *   in a room:           a bar with the room's name and the way back (the host can also close all rooms).
 */
export function BreakoutController({ meetingId, parentId, isHost, people, kind }: {
    meetingId: string;
    /** Set when this room IS a breakout room: the main meeting's id. */
    parentId: string | null;
    isHost: boolean;
    /** Everyone else in this room right now. */
    people: Person[];
    kind: MeetingKind;
}) {
    const token = useAccessToken();
    const qc = useQueryClient();
    const navigate = useNavigate();
    const mainId = parentId ?? meetingId;
    const arrivedAt = useRef(Date.now());
    const [dialog, setDialog] = useState(false);
    const [dismissed, setDismissed] = useState<string | null>(null);
    const me = normalizeUserId(useAuth().user?.profile.sub);

    // Commands live in the main meeting's chat; every room polls it.
    const chat = useQuery({
        queryKey: ["breakout-commands", mainId, token],
        queryFn: () => connectParityApi.listMeetingMessages(mainId, token),
        enabled: Boolean(token),
        refetchInterval: 4_000
    });
    const latest = useMemo(() => {
        const list = chat.data ?? [];
        for (let i = list.length - 1; i >= 0; i--) {
            const cmd = parseCommand(list[i]!.bodyHtml);
            if (cmd) return { id: list[i]!.id, at: new Date(list[i]!.sentAt).getTime(), cmd };
        }
        return null;
    }, [chat.data]);
    const open = latest?.cmd.t === "open" ? latest.cmd : null;

    useEffect(() => {
        const on = () => { if (isHost) setDialog(true); };
        window.addEventListener(OPEN_BREAKOUT_EVENT, on);
        return () => window.removeEventListener(OPEN_BREAKOUT_EVENT, on);
    }, [isHost]);

    // Main meeting: I was sent to a room.
    const mine = open?.rooms.find((r) => r.members.includes(me)) ?? null;
    const showPrompt = !parentId && mine && latest && dismissed !== latest.id && !isHost;

    // In a room: the host closed the rooms after I arrived.
    const closing = Boolean(parentId && latest?.cmd.t === "close" && latest.at > arrivedAt.current - 2_000);
    const [count, setCount] = useState(10);
    useEffect(() => {
        if (!closing) return;
        setCount(10);
        const t = window.setInterval(() => setCount((c) => c - 1), 1000);
        return () => window.clearInterval(t);
    }, [closing, latest?.id]);
    useEffect(() => {
        if (closing && count <= 0) navigate(`/meeting/${mainId}?bo=1`, { replace: true });
    }, [closing, count, navigate, mainId]);

    const goTo = (roomId: string) => { setDialog(false); navigate(`/meeting/${roomId}?bo=1`); };

    const roomName = parentId ? (open?.rooms.find((r) => r.id === meetingId)?.name ?? "Breakout room") : null;

    const closeAll = async () => {
        await connectParityApi.sendMeetingMessage(mainId, commandHtml({ t: "close" }, "Breakout rooms are closing. Everyone is returning to the main meeting."), token);
        await qc.invalidateQueries({ queryKey: ["breakout-commands", mainId] });
    };

    return (
        <>
            {parentId ? (
                <div className="pointer-events-auto fixed left-1/2 top-3 z-40 flex -translate-x-1/2 items-center gap-3 rounded-full border border-white/15 bg-zinc-900/95 px-4 py-1.5 text-xs text-white shadow-lg" role="status">
                    <UsersRound className="h-4 w-4 text-sky-300" aria-hidden />
                    <span>{closing ? `Rooms closed. Returning to the main meeting in ${Math.max(count, 0)}s` : roomName}</span>
                    <button type="button" onClick={() => navigate(`/meeting/${mainId}?bo=1`, { replace: true })} className="rounded-full bg-white/10 px-3 py-0.5 font-medium hover:bg-white/20">Return to main meeting</button>
                    {isHost && !closing ? (
                        <button type="button" onClick={() => void closeAll()} className="rounded-full bg-red-600/80 px-3 py-0.5 font-medium hover:bg-red-600">Close all rooms</button>
                    ) : null}
                </div>
            ) : null}

            {showPrompt && mine ? (
                <div role="alertdialog" aria-label="Breakout room invitation" className="pointer-events-auto fixed left-1/2 top-16 z-50 w-[22rem] max-w-[calc(100vw-2rem)] -translate-x-1/2 rounded-lg border border-white/15 bg-zinc-900 p-4 text-white shadow-2xl">
                    <p className="text-sm font-semibold">You're invited to {mine.name}</p>
                    <p className="mt-1 text-xs text-white/70">The host opened breakout rooms. You can come back to the main meeting at any time.</p>
                    <div className="mt-3 flex justify-end gap-2">
                        <button type="button" onClick={() => setDismissed(latest!.id)} className="rounded-md px-3 py-1.5 text-xs hover:bg-white/10">Not now</button>
                        <button type="button" onClick={() => goTo(mine.id)} className="rounded-md bg-sky-600 px-3 py-1.5 text-xs font-medium hover:bg-sky-500">Join room</button>
                    </div>
                </div>
            ) : null}

            {dialog && isHost ? (
                <HostDialog
                    meetingId={meetingId}
                    kind={kind}
                    people={people.filter((p) => p.userId)}
                    open={open}
                    onClose={() => setDialog(false)}
                    onCloseAll={closeAll}
                    onOpened={async (cmd, names) => {
                        await connectParityApi.sendMeetingMessage(mainId, commandHtml(cmd, `Breakout rooms are open: ${names}.`), token);
                        await qc.invalidateQueries({ queryKey: ["breakout-commands", mainId] });
                    }}
                    onJoin={goTo}
                />
            ) : null}
        </>
    );
}

function HostDialog({ meetingId, kind, people, open, onClose, onCloseAll, onOpened, onJoin }: {
    meetingId: string;
    kind: MeetingKind;
    people: Person[];
    open: Extract<BreakoutCommand, { t: "open" }> | null;
    onClose: () => void;
    onCloseAll: () => Promise<void>;
    onOpened: (cmd: BreakoutCommand, names: string) => Promise<void>;
    onJoin: (roomId: string) => void;
}) {
    const token = useAccessToken();
    const [count, setCount] = useState(2);
    const [mode, setMode] = useState<"auto" | "manual">("auto");
    const [manual, setManual] = useState<Record<string, number>>({});
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [onClose]);

    const assignment = (): number[] => people.map((p, i) => (mode === "auto" ? i % count : Math.min(manual[normalizeUserId(p.userId)] ?? -1, count - 1)));

    const create = async () => {
        setBusy(true);
        setError(null);
        try {
            const where = assignment();
            const rooms: BreakoutRoom[] = [];
            for (let i = 0; i < count; i++) {
                const name = `Room ${i + 1}`;
                const m = await connectParityApi.createMeeting({
                    subject: `${BREAKOUT_SUBJECT_PREFIX}${name}`,
                    scheduledStart: new Date().toISOString(),
                    description: breakoutDescription(meetingId),
                    kind
                }, token);
                await connectParityApi.startMeeting(m.id, token);
                rooms.push({ id: m.id, name, members: people.filter((_, k) => where[k] === i).map((p) => normalizeUserId(p.userId)) });
            }
            await onOpened({ t: "open", rooms }, rooms.map((r) => `${r.name} (${r.members.length})`).join(", "));
        } catch {
            setError("Couldn't open the rooms. Try again.");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div role="dialog" aria-modal="true" aria-label="Breakout rooms" className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onMouseDown={(e) => { if (e.target === e.currentTarget) onClose(); }}>
            <div className="flex max-h-[85vh] w-full max-w-lg flex-col overflow-hidden rounded-lg border border-white/15 bg-zinc-900 text-white shadow-2xl">
                <header className="flex items-center justify-between border-b border-white/10 px-4 py-3">
                    <h2 className="text-sm font-semibold">Breakout rooms</h2>
                    <button type="button" onClick={onClose} aria-label="Close" className="rounded p-1 hover:bg-white/10"><X className="h-4 w-4" /></button>
                </header>
                <div className="min-h-0 flex-1 space-y-4 overflow-y-auto p-4 text-sm">
                    {open ? (
                        <>
                            <p className="text-white/70">Rooms are open. People assigned to a room were invited to join it.</p>
                            <ul className="space-y-1.5">
                                {open.rooms.map((r) => (
                                    <li key={r.id} className="flex items-center gap-2 rounded-md border border-white/10 px-3 py-2">
                                        <span className="flex-1">{r.name}<span className="ml-2 text-xs text-white/60">{r.members.length} invited</span></span>
                                        <button type="button" onClick={() => onJoin(r.id)} className="rounded bg-white/10 px-2 py-1 text-xs hover:bg-white/20">Join</button>
                                    </li>
                                ))}
                            </ul>
                            <div className="flex justify-end">
                                <button type="button" disabled={busy} onClick={async () => { setBusy(true); try { await onCloseAll(); onClose(); } finally { setBusy(false); } }} className="rounded-md bg-red-600 px-3 py-1.5 text-xs font-medium hover:bg-red-500 disabled:opacity-50">Close all rooms</button>
                            </div>
                        </>
                    ) : (
                        <>
                            <div className="flex items-center gap-3">
                                <label htmlFor="bo-count" className="text-white/80">Number of rooms</label>
                                <input id="bo-count" type="number" min={1} max={10} value={count} onChange={(e) => setCount(Math.min(10, Math.max(1, Number(e.target.value) || 1)))} className="h-8 w-16 rounded-md border border-white/20 bg-zinc-800 px-2" />
                            </div>
                            <div role="radiogroup" aria-label="Assign participants" className="flex gap-2">
                                {([["auto", "Assign automatically"], ["manual", "Assign manually"]] as const).map(([v, label]) => (
                                    <button key={v} type="button" role="radio" aria-checked={mode === v} onClick={() => setMode(v)} className={cn("rounded-md border px-3 py-1.5 text-xs", mode === v ? "border-sky-400 bg-sky-500/20" : "border-white/20 hover:bg-white/10")}>{label}</button>
                                ))}
                            </div>
                            {people.length === 0 ? (
                                <p className="text-xs text-white/60">Nobody else is in the meeting yet. Rooms will open empty; people can join one from the invite list.</p>
                            ) : mode === "manual" ? (
                                <ul className="space-y-1.5">
                                    {people.map((p) => (
                                        <li key={p.userId} className="flex items-center gap-2">
                                            <span className="min-w-0 flex-1 truncate">{p.displayName}</span>
                                            <select
                                                aria-label={`Room for ${p.displayName}`}
                                                value={Math.min(manual[normalizeUserId(p.userId)] ?? -1, count - 1)}
                                                onChange={(e) => setManual((m) => ({ ...m, [normalizeUserId(p.userId)]: Number(e.target.value) }))}
                                                className="h-8 rounded-md border border-white/20 bg-zinc-800 px-2 text-xs"
                                            >
                                                <option value={-1}>Stay in main meeting</option>
                                                {Array.from({ length: count }, (_, i) => <option key={i} value={i}>Room {i + 1}</option>)}
                                            </select>
                                        </li>
                                    ))}
                                </ul>
                            ) : (
                                <p className="text-xs text-white/60">{people.length} {people.length === 1 ? "person" : "people"} will be split evenly across {count} {count === 1 ? "room" : "rooms"}.</p>
                            )}
                            {error ? <p role="alert" className="text-xs text-red-300">{error}</p> : null}
                            <div className="flex justify-end">
                                <button type="button" disabled={busy} onClick={() => void create()} className="inline-flex items-center gap-1.5 rounded-md bg-sky-600 px-4 py-1.5 text-xs font-medium hover:bg-sky-500 disabled:opacity-50">
                                    {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null} Open rooms
                                </button>
                            </div>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
